/**
 * SubsidyRenewalDialog - renew a Beitragsfaktor or SBU confirmation from a row
 * of the dashboard widget (#2004), without opening the contract.
 *
 * A new BF becomes a new contract version from its valid-from day (the existing
 * ScheduleChange); an SBU-only renewal updates the live version in place. The
 * optional confirmations are uploaded to the version that carries the renewal
 * (#2002). See useSubsidyRenewal for why the two paths differ.
 *
 * A KmbB row (#2007) renews the yearly review meeting instead: a new cycle end
 * on the live version and, optionally, the two KmbB papers with their dates.
 *
 * No <form> element, like SubsidyDocumentUploadDialog: one action, plain state.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { addYears } from "date-fns";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { Trans } from "@lingui/react/macro";
import { FileText, Loader2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { BlockingInvoicesDialog } from "@/components/billing/contracts/BlockingInvoicesDialog";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useCancelAndProceed } from "@/hooks/useCancelAndProceed";
import {
  buildRenewalPlan,
  useSubsidyRenewal,
  type RenewalPlan,
  type RenewalUpload,
} from "@/hooks/useSubsidyRenewal";
import { images_GenerateUploadURLRequest } from "@/api/models/images_GenerateUploadURLRequest";
import type { billing_ExpiringBFContractDTO } from "@/api/models/billing_ExpiringBFContractDTO";
import { parseBlockingInvoicesError, type BlockingInvoiceRef } from "@/utils/contractErrorMessages";
import { formatDate } from "@/utils/dateUtils";
import { defaultValidUntil } from "@/components/billing/subsidy/subsidyDocumentCategories";

const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

interface PickedFile {
  fileId: string;
  fileName: string;
}

/** One optional confirmation file, uploaded at once through the presigned flow. */
function ConfirmationFile({
  id,
  label,
  file,
  onChange,
  onBusyChange,
}: {
  id: string;
  label: React.ReactNode;
  file: PickedFile | null;
  onChange: (file: PickedFile | null) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { i18n } = useLingui();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingName = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { uploadFile, uploadProgress, isUploading, reset, cancelUpload } = useImageUpload({
    purpose: images_GenerateUploadURLRequest.purpose.DOCUMENT,
    silent: true,
    onSuccess: (fileId) => onChange({ fileId, fileName: pendingName.current ?? "" }),
    onError: () => {
      pendingName.current = null;
    },
  });

  useEffect(() => onBusyChange(isUploading), [isUploading, onBusyChange]);

  const pick = async (files: FileList | null) => {
    const picked = files?.[0];
    if (!picked) return;
    if (!ACCEPTED_TYPES.includes(picked.type)) {
      setError(i18n._(msg({ id: "subsidyDocuments.dialog.file.type", message: "Only PDF, JPEG or PNG files." })));
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setError(null);
    pendingName.current = picked.name;
    await uploadFile(picked);
  };

  const remove = () => {
    if (isUploading) cancelUpload();
    reset();
    onChange(null);
    pendingName.current = null;
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {file ? (
        <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm" data-testid={`${id}-selected`}>
          <span className="flex min-w-0 items-center gap-2">
            <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{file.fileName}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={remove}
            aria-label={i18n._(msg({ id: "subsidyDocuments.dialog.file.remove", message: "Remove file" }))}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            ref={inputRef}
            id={id}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            data-testid={`${id}-input`}
            onChange={(e) => void pick(e.target.files)}
          />
          <Button type="button" variant="outline" className="w-full" disabled={isUploading} onClick={() => inputRef.current?.click()}>
            {isUploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            <Trans id="subsidyRenewal.file.choose">Choose file (optional)</Trans>
          </Button>
          {isUploading ? <Progress value={uploadProgress.progress} /> : null}
        </div>
      )}
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface SubsidyRenewalDialogProps {
  row: billing_ExpiringBFContractDTO | null;
  onOpenChange: (open: boolean) => void;
}

export function SubsidyRenewalDialog({ row, onOpenChange }: SubsidyRenewalDialogProps) {
  const { i18n } = useLingui();
  const renewal = useSubsidyRenewal();
  const cancelAndProceed = useCancelAndProceed();
  const [bfPercent, setBfPercent] = useState("");
  const [bfValidFrom, setBfValidFrom] = useState<Date | undefined>();
  const [bfValidUntil, setBfValidUntil] = useState<Date | undefined>();
  const [sbuValidUntil, setSbuValidUntil] = useState<Date | undefined>();
  const [bfFile, setBfFile] = useState<PickedFile | null>(null);
  const [sbuFile, setSbuFile] = useState<PickedFile | null>(null);
  const [bfUploading, setBfUploading] = useState(false);
  const [sbuUploading, setSbuUploading] = useState(false);
  const [kmbbValidUntil, setKmbbValidUntil] = useState<Date | undefined>();
  const [kmbbMeetingOn, setKmbbMeetingOn] = useState<Date | undefined>();
  const [kmbbFachDated, setKmbbFachDated] = useState<Date | undefined>();
  const [kmbbProtocolFile, setKmbbProtocolFile] = useState<PickedFile | null>(null);
  const [kmbbFachFile, setKmbbFachFile] = useState<PickedFile | null>(null);
  const [kmbbProtocolUploading, setKmbbProtocolUploading] = useState(false);
  const [kmbbFachUploading, setKmbbFachUploading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [collision, setCollision] = useState<{ invoices: BlockingInvoiceRef[] } | null>(null);
  const lastRequest = useRef<{ plan: RenewalPlan; uploads: RenewalUpload[] } | null>(null);

  const open = row !== null;
  const rowKey = row ? `${row.contract_id}-${row.document}` : "";
  const rowKmbbUntil = row?.kmbb_valid_until;
  // Every opening starts from an empty form.
  useEffect(() => {
    if (!rowKey) return;
    setBfPercent("");
    setBfValidFrom(undefined);
    setBfValidUntil(undefined);
    setSbuValidUntil(undefined);
    setBfFile(null);
    setSbuFile(null);
    // A KmbB cycle runs one more year from the current end.
    setKmbbValidUntil(rowKmbbUntil ? addYears(new Date(rowKmbbUntil), 1) : undefined);
    setKmbbMeetingOn(undefined);
    setKmbbFachDated(undefined);
    setKmbbProtocolFile(null);
    setKmbbFachFile(null);
    setPlanError(null);
    setCollision(null);
  }, [rowKey, rowKmbbUntil]);

  const isKmbb = row?.document === "kmbb";
  const kmbbUntil = row?.kmbb_valid_until ? formatDate(row.kmbb_valid_until) : "";

  const childName = row ? [row.child_first_name, row.child_last_name].filter(Boolean).join(" ") : "";
  const sbuDays = row?.attendance_days_per_week;
  const bfUntil = row?.beitragsfaktor_valid_until ? formatDate(row.beitragsfaktor_valid_until) : "";
  const sbuUntil = row?.sbu_valid_until ? formatDate(row.sbu_valid_until) : "";
  const contractNumber = row?.contract_number ?? "";

  const close = useCallback(() => {
    setCollision(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleBfValidFrom = (date: Date | undefined) => {
    setBfValidFrom(date);
    // A BF confirmation is valid for one year; never overwrite a value the user set.
    if (date && !bfValidUntil) setBfValidUntil(defaultValidUntil("bf_confirmation", date));
  };

  const submitKmbb = async () => {
    if (!row) return;
    const plan = buildRenewalPlan(
      { row, kmbbValidUntil, uploads: [] },
      i18n._(msg({ id: "subsidyRenewal.kmbb.reason", message: "KmbB review meeting" })),
    );
    if (typeof plan === "string") {
      setPlanError(i18n._(msg({ id: "subsidyRenewal.kmbb.error.nothing", message: "Enter the new end of the KmbB cycle." })));
      return;
    }
    setPlanError(null);
    const uploads: RenewalUpload[] = [];
    if (kmbbProtocolFile) {
      uploads.push({
        category: "kmbb_standortgespraech",
        fileId: kmbbProtocolFile.fileId,
        validFrom: kmbbMeetingOn,
        validUntil: kmbbMeetingOn ? defaultValidUntil("kmbb_standortgespraech", kmbbMeetingOn) : undefined,
      });
    }
    if (kmbbFachFile) {
      uploads.push({
        category: "kmbb_fachstelle",
        fileId: kmbbFachFile.fileId,
        validFrom: kmbbFachDated,
        validUntil: kmbbFachDated ? defaultValidUntil("kmbb_fachstelle", kmbbFachDated) : undefined,
      });
    }
    lastRequest.current = { plan, uploads };
    try {
      await renewal.mutateAsync({ plan, row, uploads });
      close();
    } catch (error) {
      const blocking = parseBlockingInvoicesError(error);
      if (blocking) setCollision(blocking);
    }
  };

  const submit = async () => {
    if (!row) return;
    if (isKmbb) {
      await submitKmbb();
      return;
    }
    const pct = bfPercent.trim() === "" ? undefined : Number.parseFloat(bfPercent);
    if (pct !== undefined && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      setPlanError(i18n._(msg({ id: "subsidyRenewal.error.bfRange", message: "The Beitragsfaktor is a percentage between 0 and 100." })));
      return;
    }
    const plan = buildRenewalPlan(
      {
        row,
        beitragsfaktor: pct === undefined ? undefined : Math.round((pct / 100) * 10000) / 10000,
        bfValidFrom,
        bfValidUntil,
        sbuValidUntil,
        uploads: [],
      },
      i18n._(msg({ id: "subsidyRenewal.reason", message: "Beitragsfaktor / SBU renewal" })),
    );
    if (typeof plan === "string") {
      setPlanError(
        plan === "bfIncomplete"
          ? i18n._(msg({ id: "subsidyRenewal.error.bfIncomplete", message: "A new Beitragsfaktor needs its value, valid from and valid until." }))
          : plan === "bfOrder"
            ? i18n._(msg({ id: "subsidyRenewal.error.bfOrder", message: "\"Valid from\" must not be after \"valid until\"." }))
            : i18n._(msg({ id: "subsidyRenewal.error.nothing", message: "Enter a new Beitragsfaktor or a new SBU valid-until date." })),
      );
      return;
    }
    setPlanError(null);
    const uploads: RenewalUpload[] = [];
    if (bfFile) {
      uploads.push({ category: "bf_confirmation", fileId: bfFile.fileId, validFrom: bfValidFrom, validUntil: bfValidUntil });
    }
    if (sbuFile) {
      uploads.push({ category: "sbu_confirmation", fileId: sbuFile.fileId, validUntil: sbuValidUntil });
    }
    lastRequest.current = { plan, uploads };
    try {
      await renewal.mutateAsync({ plan, row, uploads });
      close();
    } catch (error) {
      const blocking = parseBlockingInvoicesError(error);
      if (blocking) setCollision(blocking);
    }
  };

  const handleCancelAndProceed = async () => {
    const last = lastRequest.current;
    if (!collision || !last || !row) return;
    try {
      await cancelAndProceed.run(
        collision.invoices.map((inv) => inv.id),
        () => renewal.mutateAsync({ plan: last.plan, row, uploads: last.uploads }),
        collision.invoices.map((inv) => inv.period),
      );
      close();
    } catch (error) {
      setCollision(parseBlockingInvoicesError(error));
    }
  };

  const busy = renewal.isPending || bfUploading || sbuUploading || kmbbProtocolUploading || kmbbFachUploading;

  return (
    <>
      <Dialog open={open && collision === null} onOpenChange={(next) => !busy && !next && close()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="subsidy-renewal-dialog">
          {isKmbb ? (
          <>
          <DialogHeader>
            <DialogTitle>
              <Trans id="subsidyRenewal.kmbb.title">Renew the KmbB review meeting</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans id="subsidyRenewal.kmbb.description">
                {childName}, contract {contractNumber}. The new cycle end updates the current version.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5" data-testid="subsidy-renewal-kmbb">
            {kmbbUntil ? (
              <p className="text-sm text-muted-foreground">
                <Trans id="subsidyRenewal.kmbb.current">Current cycle ends on {kmbbUntil}</Trans>
              </p>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="subsidy-renewal-kmbb-until">
                <Trans id="subsidyRenewal.kmbb.validUntil">New KmbB cycle ends on</Trans>
              </Label>
              <DatePicker
                id="subsidy-renewal-kmbb-until"
                value={kmbbValidUntil}
                onChange={setKmbbValidUntil}
                showClearButton
                data-testid="subsidy-renewal-kmbb-until"
              />
            </div>
            <section className="space-y-3 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="subsidy-renewal-kmbb-meeting">
                  <Trans id="subsidyRenewal.kmbb.meetingOn">Review meeting on</Trans>
                </Label>
                <DatePicker
                  id="subsidy-renewal-kmbb-meeting"
                  value={kmbbMeetingOn}
                  onChange={setKmbbMeetingOn}
                  showClearButton
                  data-testid="subsidy-renewal-kmbb-meeting"
                />
              </div>
              <ConfirmationFile
                id="subsidy-renewal-kmbb-protocol-file"
                label={<Trans id="subsidyRenewal.kmbb.protocolFile">KmbB review meeting record</Trans>}
                file={kmbbProtocolFile}
                onChange={setKmbbProtocolFile}
                onBusyChange={setKmbbProtocolUploading}
              />
            </section>
            <section className="space-y-3 border-t pt-4">
              <div className="space-y-2">
                <Label htmlFor="subsidy-renewal-kmbb-fach-dated">
                  <Trans id="subsidyRenewal.kmbb.fachDated">Specialist confirmation dated</Trans>
                </Label>
                <DatePicker
                  id="subsidy-renewal-kmbb-fach-dated"
                  value={kmbbFachDated}
                  onChange={setKmbbFachDated}
                  showClearButton
                  data-testid="subsidy-renewal-kmbb-fach-dated"
                />
              </div>
              <ConfirmationFile
                id="subsidy-renewal-kmbb-fach-file"
                label={<Trans id="subsidyRenewal.kmbb.fachFile">KmbB specialist confirmation</Trans>}
                file={kmbbFachFile}
                onChange={setKmbbFachFile}
                onBusyChange={setKmbbFachUploading}
              />
            </section>
            {planError ? (
              <p className="text-sm text-destructive" role="alert" data-testid="subsidy-renewal-error">
                {planError}
              </p>
            ) : null}
          </div>
          </>
          ) : (
          <>
          <DialogHeader>
            <DialogTitle>
              <Trans id="subsidyRenewal.title">Renew Beitragsfaktor or SBU</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans id="subsidyRenewal.description">
                {childName}, contract {contractNumber}. A new Beitragsfaktor starts a new contract version on
                its valid-from day. A new SBU date alone updates the current version.
              </Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <section className="space-y-3" aria-labelledby="subsidy-renewal-bf">
              <h3 id="subsidy-renewal-bf" className="text-sm font-medium">
                <Trans id="subsidyRenewal.bf.heading">Beitragsfaktor</Trans>
              </h3>
              {bfUntil ? (
                <p className="text-sm text-muted-foreground" data-testid="subsidy-renewal-bf-current">
                  <Trans id="subsidyRenewal.bf.current">Current: valid until {bfUntil}</Trans>
                </p>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="subsidy-renewal-bf-value">
                  <Trans id="subsidyRenewal.bf.value">New Beitragsfaktor (%)</Trans>
                </Label>
                <Input
                  id="subsidy-renewal-bf-value"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  max={100}
                  value={bfPercent}
                  onChange={(e) => setBfPercent(e.target.value)}
                  data-testid="subsidy-renewal-bf-value"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="subsidy-renewal-bf-from">
                    <Trans id="subsidyRenewal.bf.validFrom">Valid from</Trans>
                  </Label>
                  <DatePicker
                    id="subsidy-renewal-bf-from"
                    value={bfValidFrom}
                    onChange={handleBfValidFrom}
                    showClearButton
                    data-testid="subsidy-renewal-bf-from"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subsidy-renewal-bf-until">
                    <Trans id="subsidyRenewal.bf.validUntil">Valid until</Trans>
                  </Label>
                  <DatePicker
                    id="subsidy-renewal-bf-until"
                    value={bfValidUntil}
                    onChange={setBfValidUntil}
                    showClearButton
                    data-testid="subsidy-renewal-bf-until"
                  />
                </div>
              </div>
              <ConfirmationFile
                id="subsidy-renewal-bf-file"
                label={<Trans id="subsidyRenewal.bf.file">BF confirmation</Trans>}
                file={bfFile}
                onChange={setBfFile}
                onBusyChange={setBfUploading}
              />
            </section>

            <section className="space-y-3 border-t pt-4" aria-labelledby="subsidy-renewal-sbu">
              <h3 id="subsidy-renewal-sbu" className="text-sm font-medium">
                <Trans id="subsidyRenewal.sbu.heading">SBU</Trans>
              </h3>
              {/* Read-only mirror, as on the contract (#2001): the SBU days are the contracted days. */}
              <p className="text-sm text-muted-foreground" data-testid="subsidy-renewal-sbu-days">
                {typeof sbuDays === "number" ? (
                  <Trans id="subsidyRenewal.sbu.days">SBU days per week: {sbuDays} (from the contracted days)</Trans>
                ) : (
                  <Trans id="subsidyRenewal.sbu.daysUnknown">SBU days per week: not set on the contract</Trans>
                )}
              </p>
              {sbuUntil ? (
                <p className="text-sm text-muted-foreground">
                  <Trans id="subsidyRenewal.sbu.current">Current: valid until {sbuUntil}</Trans>
                </p>
              ) : null}
              <div className="space-y-2">
                <Label htmlFor="subsidy-renewal-sbu-until">
                  <Trans id="subsidyRenewal.sbu.validUntil">New SBU valid until</Trans>
                </Label>
                <DatePicker
                  id="subsidy-renewal-sbu-until"
                  value={sbuValidUntil}
                  onChange={setSbuValidUntil}
                  showClearButton
                  data-testid="subsidy-renewal-sbu-until"
                />
              </div>
              <ConfirmationFile
                id="subsidy-renewal-sbu-file"
                label={<Trans id="subsidyRenewal.sbu.file">SBU confirmation</Trans>}
                file={sbuFile}
                onChange={setSbuFile}
                onBusyChange={setSbuUploading}
              />
            </section>

            {planError ? (
              <p className="text-sm text-destructive" role="alert" data-testid="subsidy-renewal-error">
                {planError}
              </p>
            ) : null}
          </div>
          </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={close}>
              <Trans id="common.cancel">Cancel</Trans>
            </Button>
            <Button type="button" disabled={busy} onClick={() => void submit()} data-testid="subsidy-renewal-save">
              {renewal.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              <Trans id="subsidyRenewal.save">Save renewal</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockingInvoicesDialog
        open={collision !== null}
        invoices={collision?.invoices ?? []}
        onCancelAndProceed={handleCancelAndProceed}
        onClose={() => setCollision(null)}
        isProcessing={cancelAndProceed.isProcessing || renewal.isPending}
      />
    </>
  );
}

export default SubsidyRenewalDialog;
